import { create } from 'zustand';

type ThemeMode = 'dark' | 'light' | 'system';

interface ThemeState {
  mode: ThemeMode;
  resolved: 'dark' | 'light';
  setMode: (mode: ThemeMode) => void;
}

const getSystemTheme = (): 'dark' | 'light' =>
  window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

const resolve = (mode: ThemeMode): 'dark' | 'light' =>
  mode === 'system' ? getSystemTheme() : mode;

const saved = (localStorage.getItem('fh-theme') as ThemeMode) || 'dark';

export const useThemeStore = create<ThemeState>((set) => ({
  mode: saved,
  resolved: resolve(saved),
  setMode: (mode) => {
    localStorage.setItem('fh-theme', mode);
    const resolved = resolve(mode);
    applyTheme(resolved);
    set({ mode, resolved });
  },
}));

function applyTheme(theme: 'dark' | 'light') {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);

  if (theme === 'light') {
    root.style.setProperty('--ion-background-color', '#fbf9f4');
    root.style.setProperty('--ion-text-color', '#1b1c19');
    root.style.setProperty('--ion-card-background', '#ffffff');
    root.style.setProperty('--ion-item-background', '#ffffff');
    root.style.setProperty('--ion-toolbar-background', '#fbf9f4');
    document.body.style.backgroundColor = '#fbf9f4';
    document.body.style.color = '#1b1c19';
  } else {
    root.style.setProperty('--ion-background-color', '#0f1923');
    root.style.setProperty('--ion-text-color', '#ffffff');
    root.style.setProperty('--ion-card-background', '#1a2a3a');
    root.style.setProperty('--ion-item-background', '#162231');
    root.style.setProperty('--ion-toolbar-background', '#0f1923');
    document.body.style.backgroundColor = '#0f1923';
    document.body.style.color = '#ffffff';
  }
}

// Apply on load
applyTheme(resolve(saved));

// Listen for system theme changes
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
  const { mode } = useThemeStore.getState();
  if (mode === 'system') {
    const resolved = getSystemTheme();
    applyTheme(resolved);
    useThemeStore.setState({ resolved });
  }
});
